<template>
  <div class="container">
    <header class="text-center wizard-header-text">
      <div class="title-with-badge">
        <h1 class="gb-page-title">Assessment Wizard</h1>
        <span class="badge-wrapper">
          <BetaBadge size="small" />
        </span>
      </div>
      <p class="title-subheading">Discover targeted baseline and progress monitoring assessments from thousands of resources in our collection. </p>
    </header>

    <AssessmentWizardOverlay :theme="loading || agenticError ? 'white' : 'gray'">
      <AssessmentLoadingScreen
        v-if="loading || agenticError"
        :has-error="!!agenticError"
        @restart="handleReturnToWizard" />
      <form
        v-else
        @submit.prevent="handleSubmit"
        class="wizard-form">
        <!-- Form Inputs Container -->
        <div class="form-inputs-container">
          <!-- Grade Level -->
          <div class="form-field">
            <label id="grade-level-label" class="form-label">Grade Level</label>
            <a11y-dropdown
              :dropdown-props="{ tag: 'div' }"
              :button-props="{ id: 'grade-filter-btn', 'data-role': 'trigger', class: 'dropdown-toggle btn btn-default', 'aria-labelledby': 'grade-level-label grade-selection' }"
              :dom-attributes="{ role: 'menu', id: 'grade-dropdown' }">
              <template slot="button-contents">
                <span id="grade-selection" :class="{ 'text-muted': !formData.filters.gradeLevel }">
                  {{ gradeSelectorText }}
                  <span class="caret" />
                </span>
              </template>
              <template slot="dropdown-items">
                <li
                  class="dropdown-item"
                  role="menuitem">
                  <a
                    role="button"
                    aria-label="Select any grade level"
                    @click="updateFilters({ field: 'gradeLevel', value: '' })">
                    Any grade
                  </a>
                </li>
                <li
                  v-for="grade in grades"
                  :key="grade"
                  class="dropdown-item"
                  role="menuitem">
                  <a
                    role="button"
                    :aria-label="`Select grade ${grade}`"
                    @click="updateFilters({ field: 'gradeLevel', value: grade })">
                    {{ grade }}
                  </a>
                </li>
              </template>
            </a11y-dropdown>
          </div>

          <!-- Natural Language Query -->
          <div class="form-field">
            <label for="assessment-query" class="form-label">Describe the assessment you need</label>
            <div class="textarea-container">
              <textarea
                id="assessment-query"
                ref="queryTextarea"
                v-model="formData.query"
                rows="3"
                :class="['form-textarea', { 'error-border': showQueryError }]"
                placeholder="Example: Phonics assessments for consonant blends and digraphs"
                aria-label="Describe the assessment you need"
                aria-required="true"
                :aria-invalid="showQueryError ? 'true' : 'false'"
                :aria-describedby="showQueryError ? 'query-error' : null"
                @input="autoExpandTextarea"
              />

              <!-- File Upload Section -->
              <div :class="['file-upload-section', { 'has-file': formData.files.length }]">
                <input
                  ref="fileInput"
                  type="file"
                  class="file-input-hidden"
                  accept=".pdf,.jpg,.jpeg,.png"
                  aria-label="Upload a file (PDF, JPG, or PNG, max 10MB)"
                  :aria-describedby="fileUploadError ? 'file-upload-error' : null"
                  :aria-invalid="fileUploadError ? 'true' : 'false'"
                  @change="handleFileUpload"
                >

                <div v-if="!formData.files.length" class="file-upload-trigger">
                  <button
                    type="button"
                    class="upload-button"
                    @click="$refs.fileInput.click()"
                    aria-label="Upload a file"
                    :aria-describedby="fileUploadError ? 'file-upload-error' : null">
                    <i class="material-icons plus-icon">add</i>
                  </button>
                  <div class="upload-text">
                    <p class="upload-label">
                      <span class="label-text">Upload a file</span>
                      <span class="optional-text"> (optional)</span>
                      <span class="info-icon-wrapper">
                        <tooltip
                          text="Your file will not be stored permanently. Files are processed in real time and immediately discarded."
                          target=".file-upload-info-icon"
                          aria-label="File privacy information"
                          placement="top">
                          <i class="material-icons-round info-icon file-upload-info-icon" tabindex="0">info_outline</i>
                        </tooltip>
                      </span>
                    </p>
                    <p class="upload-helper-text">
                      Attach a score report (e.g. i-Ready) or student work sample for better results.
                    </p>
                  </div>
                </div>

                <div v-else class="file-chip">
                  <div class="file-chip-content">
                    <img
                      :src="googleDocsIcon"
                      alt=""
                      class="file-chip-icon" >
                    <span class="file-chip-name">{{ formData.files[0].name }}</span>
                  </div>
                  <button
                    type="button"
                    class="file-chip-remove"
                    @click="removeFile(0)"
                    aria-label="Remove file">
                    <i class="material-icons">close</i>
                  </button>
                </div>
              </div>
            </div>
            <div v-if="formData.files.length" class="file-info-note">
              <p>
                <strong>Your file will not be stored:</strong> Files are processed in real time and immediately discarded. The feature does not store files to our servers.
              </p>
            </div>
            <div v-show="showQueryError" class="field-error query-error">
              <i class="material-icons-outlined error-icon" aria-hidden="true">report_problem</i>
              <span
                id="query-error"
                role="alert"
                aria-live="assertive">
                <template v-if="showQueryError">Assessment description is required</template>
              </span>
            </div>
            <div v-show="fileUploadError" class="file-upload-error">
              <i class="material-icons-outlined warning-icon" aria-hidden="true">warning_amber</i>
              <span
                id="file-upload-error"
                role="alert"
                aria-live="assertive">
                <template v-if="fileUploadError">{{ fileUploadError }}</template>
              </span>
            </div>
          </div>
        </div>

        <!-- Submit Button -->
        <div class="form-actions">
          <button
            type="submit"
            class="btn btn-primary"
            aria-label="Find assessments based on your description">
            Find Assessments
          </button>
        </div>
      </form>
    </AssessmentWizardOverlay>

    <!-- AI Integration Note -->
    <div class="ai-disclaimer-container">
      <AIDisclaimer variant="beta" single-line />
    </div>
    <SubmitFeedback type="Assessment Wizard" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import A11yDropdown from '@/components/A11yDropdown.vue'
import AssessmentWizardOverlay from '@/components/AssessmentWizardOverlay.vue'
import AssessmentLoadingScreen from './AssessmentLoadingScreen.vue'
import AIDisclaimer from '@/components/AIDisclaimer.vue'
import { assessmentGradesSet } from './assessments_filter_options'
import BetaBadge from '@/components/BetaBadge.vue'
import SubmitFeedback from '@/components/SubmitFeedback.vue'
import GoogleDocsIcon from '@/assets/attachments/google_docs.svg'

export default {
  name: 'AssessmentWizard',
  components: {
    AssessmentWizardOverlay,
    A11yDropdown,
    AssessmentLoadingScreen,
    AIDisclaimer,
    BetaBadge,
    SubmitFeedback
  },
  data() {
    return {
      googleDocsIcon: GoogleDocsIcon,
      formData: {
        query: '',
        files: [],
        filters: {
          subject: '',
          gradeLevel: '',
          category: '',
        },
      },
      selectedSubject: null,
      attemptedSubmit: false,
      fileUploadError: '',
    }
  },
  computed: {
    ...mapGetters({
      subjects: 'contentProfile/publishedSubjects',
      loading: 'agenticSearch/loading',
      hasResults: 'agenticSearch/hasAssessmentResults',
      agenticError: 'agenticSearch/error',
      latestSearchPrompt: 'agenticSearch/latestSearchPrompt',
      latestSearchId: 'agenticSearch/latestSearchId',
      aiFeatureVisibility: 'visibility/aiFeatureVisibility'
    }),
    subjectSelectorText() {
      return this.selectedSubject ? this.selectedSubject.subject : 'Select Subject'
    },
    categories() {
      return (this.selectedSubject && this.selectedSubject.categories) || []
    },
    grades() {
      return assessmentGradesSet
    },
    gradeSelectorText() {
      if (!this.formData.filters.gradeLevel) {
        return 'grade'
      }
      const grade = this.grades.find(g => g === this.formData.filters.gradeLevel)
      return grade ? grade : 'grade'
    },
    categorySelectorText() {
      if (!this.formData.filters.category) {
        return 'Select Category'
      }
      const category = this.categories.find(c => c === this.formData.filters.category)
      return category ? category : 'Select Category'
    },
    isFormValid() {
      const hasQuery = this.formData.query.trim().length > 0
      return hasQuery
    },
    showQueryError() {
      return this.attemptedSubmit && this.formData.query.trim().length === 0
    },
  },
  mounted() {
    const eventData = {}
    if (this.$route.query.ref) {
      eventData.ref = this.$route.query.ref
    }
    this.$sendEvent('Viewed Assessment Wizard', eventData)
    this.redirectIfAIDisabled()
  },
  methods: {
    ...mapActions({
      searchAssessments: 'agenticSearch/searchAssessments',
    }),
    redirectIfAIDisabled() {
      if (!this.aiFeatureVisibility) {
        const baseUrl = '/browse/assessments'
        const queryString = new URLSearchParams(this.$route.query).toString()
        const targetUrl = queryString ? `${baseUrl}?${queryString}` : baseUrl
        window.location.href = targetUrl
      }
    },
    autoExpandTextarea() {
      const textarea = this.$refs.queryTextarea
      if (!textarea) return

      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto'

      // Set height based on scrollHeight, with a max of 200px
      const newHeight = Math.min(textarea.scrollHeight, 200)
      textarea.style.height = newHeight + 'px'

      // Clear file upload error when user starts typing
      this.fileUploadError = ''
    },
    handleReturnToWizard() {
      // Clear store results and abort any in-flight search (eventSource.close() in clearResults)
      this.$store.dispatch('agenticSearch/clearResults')
      this.$store.commit('agenticSearch/clearLatestSearchPrompt')

      // Reset component-local form state
      this.formData.query = ''
      this.formData.files = []
      this.formData.filters.subject = ''
      this.formData.filters.gradeLevel = ''
      this.formData.filters.category = ''
      this.selectedSubject = null
      this.attemptedSubmit = false
    },
    updateFilters({ field, value }) {
      if (field === 'subject') {
        this.selectedSubject = this.subjects.find(s => s.subject === value) || null
        this.formData.filters.subject = value
        this.formData.filters.category = ''
        this.formData.filters.gradeLevel = ''
      } else {
        this.formData.filters[field] = value
      }
    },
    handleFileUpload(event) {
      const files = Array.from(event.target.files)
      this.fileUploadError = ''

      // Only allow one file as per design requirements
      if (files.length > 0) {
        const file = files[0]
        const maxSize = 10 * 1024 * 1024 // 10MB in bytes

        // Validate file size
        if (file.size > maxSize) {
          this.fileUploadError = 'File size exceeds 10MB. Please choose a smaller file.'
          event.target.value = ''
          return
        }

        // Validate file type - require both valid MIME type AND extension for security
        const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg']
        const allowedExtensions = ['.pdf', '.png', '.jpg', '.jpeg']
        const fileExtension = file.name.toLowerCase().match(/\.[^.]+$/)?.[0]

        const isValidType = allowedTypes.includes(file.type)
        const isValidExtension = allowedExtensions.includes(fileExtension)

        if (!isValidType || !isValidExtension) {
          this.fileUploadError = 'Only PDF, PNG, and JPG files are allowed.'
          event.target.value = ''
          return
        }

        this.formData.files = [file]
      }
      event.target.value = ''
    },
    removeFile(index) {
      this.formData.files.splice(index, 1)
      this.fileUploadError = ''
      this.attemptedSubmit = false
    },
    async handleSubmit() {
      this.attemptedSubmit = true
      if (!this.isFormValid) return
      try {
        const truncatedQuery = this.formData.query.length > 120
          ? this.formData.query.slice(0, 120) + '...'
          : this.formData.query
        const searchId = await this.searchAssessments({
          query: this.formData.query,
          files: this.formData.files,
          filters: this.formData.filters,
          searchGrade: this.formData.filters.gradeLevel,
        })
        this.$sendEvent('Submitted Assessment Wizard', {
          search_query: truncatedQuery,
          selected_grade: this.formData.filters.gradeLevel,
          agentic_search_id: searchId,
        })
        // Reset validation state and clear errors after successful submission
        this.attemptedSubmit = false
        this.fileUploadError = ''
      } catch (error) {
        console.error('Error searching assessments:', error)
      }
    },
  },
  watch: {
    hasResults(hasResults) {
      // Navigate as soon as we get first results
      if (hasResults) {
        this.$router.push('/browse/assessments?ref=wizard')
      }
    },
    loading(isLoading, wasLoading) {
      // Also navigate when loading completes even with 0 results
      // But only if there's still a search prompt (not cleared by restart)
      if (wasLoading && !isLoading && !this.hasResults && !this.agenticError && this.latestSearchPrompt) {
        this.$router.push('/browse/assessments?ref=wizard')
      }
    },
    'formData.query'(newValue) {
      if (this.attemptedSubmit && newValue.trim().length > 0) {
        this.attemptedSubmit = false
      }
    },
    aiFeatureVisibility() {
      this.redirectIfAIDisabled()
    }
  }
}
</script>

<style lang="less" scoped>
@import '@/variables.less';

.wizard-header-text {
  .title-with-badge {
    display: flex;
    justify-content: center;
    align-items: flex-start;
    gap: 8px;
    margin: var(--spacing-md) 0;
    width: 100%;
  }

  h1 {
    margin: 0;
  }

  .badge-wrapper {
    display: inline-flex;
    align-items: center;
    margin-top: var(--spacing-xl);
  }

  margin-bottom: var(--text-display-sm);
}

.return-button-container {
  display: flex;
  justify-content: center;
  margin: var(--text-display-sm) 0 var(--spacing-xl) 0;
}

.return-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: var(--spacing-xl) var(--text-display-sm);
  border: 1px solid var(--blue-default);
  border-radius: var(--radius-base);
  background: transparent;
  color: var(--blue-default);
  font-weight: 400;
  font-size: 16px;
  line-height: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background-color: rgba(0, 125, 186, 0.05);
  }

  &:focus {
    outline: 2px solid var(--blue-default);
    outline-offset: 2px;
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid var(--blue-default);
    outline-offset: 2px;
  }

  .arrow-left {
    font-size: var(--text-display-sm);
    line-height: 1;
  }
}

.form-inputs-container {
  display: flex;
  width: 576px;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--text-display-sm);
}

.form-field {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--spacing-md);
  width: 100%;
}

.form-label {
  font-size: var(--text-display-sm);
  font-weight: 400;
  line-height: var(--text-display-lg);
  color: var(--gray-strong);
  margin: 0;
}

.required-indicator {
  color: #d32f2f;
}

.field-error {
  display: flex;
  align-items: center;
  gap: var(--size-icon-xs);
  font-size: var(--text-md);
  font-weight: 500;
  line-height: 20px;
  color: var(--red-default);
  margin: 0;
  margin-top: calc(-1 * var(--spacing-xs));

  .error-icon {
    font-size: 20px;
    line-height: 1;
    color: var(--red-default);
    flex-shrink: 0;
  }
}

::v-deep .btn-group.dropdown {
  width: 200px;
  height: 44px;
}

::v-deep #grade-filter-btn {
  display: flex !important;
  width: 100% !important;
  height: 100% !important;
  align-items: center;
  justify-content: space-between;
  border-radius: var(--radius-base);
  border: 1px solid var(--gray-inverted-weak);
  background: #fff;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  padding: var(--spacing-xl);
  font-size: var(--text-xl);
  font-weight: 400;
  line-height: var(--text-xl);
  color: var(--gray-weak);

  > span {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    gap: var(--spacing-md);
  }

  .caret {
    margin-left: auto;
    flex-shrink: 0;
  }

  &.error-border {
    border-color: var(--red-default) !important;
  }

  &:focus {
    outline: 2px solid var(--blue-default);
    outline-offset: 2px;
    border-color: var(--blue-default);
  }

  &:focus:not(:focus-visible) {
    outline: none;
    border-color: var(--gray-inverted-weak);
  }

  &.error-border:focus:not(:focus-visible) {
    border-color: var(--red-default) !important;
  }

  &:focus-visible {
    outline: 2px solid var(--blue-default);
    outline-offset: 2px;
    border-color: var(--blue-default);
  }
}

.textarea-container {
  width: 100%;
  min-height: 160px;
  max-height: 200px;
  border: 1px solid var(--gray-inverted-weak);
  border-radius: var(--radius-base);
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 12px 14px 12px 12px;
  background-color: #fff;

  &:focus-within {
    outline: 2px solid var(--blue-default);
    outline-offset: 2px;
    border-color: var(--blue-default);
  }

  &:has(.error-border) {
    border: 1px solid var(--red-default) !important;
    box-shadow: none;
  }
}

.form-textarea {
  width: 100%;
  padding: 0;
  flex: 1 1 auto;
  min-height: 84px;
  max-height: 120px;
  border: none;
  font-size: var(--text-xl);
  font-weight: 400;
  line-height: var(--text-xl);
  resize: none;
  overflow-y: auto;
  background-color: #fff;

  &::placeholder {
    color: var(--gray-weak);
    line-height: 27px;
  }

  &:focus {
    outline: none;
  }
}

.file-upload-section {
  display: flex;
  padding-top: 16px;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  border-top: 1px solid #e0e0e0;
  background-color: #fff;

  &.has-file {
    border-top: none;
    padding-top: 16px;
  }
}

.file-input-hidden {
  display: none;
}

.file-upload-trigger {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
}

.upload-button {
  width: 28px;
  height: 28px;
  padding: 0 7px;
  border-radius: 50%;
  background-color: #f0f0f0;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #e6e6e6;
  }

  &:focus {
    outline: 2px solid var(--blue-default);
    outline-offset: 2px;
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid var(--blue-default);
    outline-offset: 2px;
  }
}

.plus-icon {
  font-size: 14px;
  line-height: 14px;
  color: #6E6E6E;
}

.upload-text {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.upload-label {
  margin: 0;
  padding: 0;
  font-size: 14px;
  line-height: 18px;
  color: #6E6E6E;
  display: flex;
  align-items: center;
  gap: 4px;

  .label-text {
    font-size: 14px;
  }

  .optional-text {
    font-size: 12px;
  }

  .info-icon-wrapper {
    margin-top: 2px;
  }

  .info-icon {
    font-size: 12px;
    color: #6E6E6E;
    cursor: help;
  }
}

.upload-helper-text {
  margin: 0;
  font-size: 12px;
  line-height: 16px;
  color: #6E6E6E;
  padding: 0;
}

.file-chip {
  display: flex;
  align-items: center;
  background-color: #f1f1f1;
  border-radius: 99px;
  padding: 6px 12px;
  max-width: 100%;
  gap: 13px;
}

.file-chip-content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.file-chip-icon {
  width: var(--size-icon-xs);
  height: var(--size-icon-xs);
  flex-shrink: 0;
}

.file-chip-name {
  font-size: var(--text-sm);
  line-height: 18px;
  color: var(--gray-default);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-chip-remove {
  width: var(--size-icon-xs);
  height: var(--size-icon-xs);
  padding: var(--spacing-md);
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  border-radius: 50%;

  &:hover {
    background-color: var(--gray-inverted-weak);
  }

  &:focus {
    outline: 2px solid var(--blue-default);
    outline-offset: 2px;
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid var(--blue-default);
    outline-offset: 2px;
  }

  .material-icons {
    font-size: var(--text-lg);
    color: var(--gray-default);
  }
}

.file-info-note {
  background-color: var(--blue-weak);
  border: 0.5px solid var(--blue-default);
  border-radius: var(--radius-base);
  padding: var(--size-icon-xs) var(--spacing-md);

  p {
    margin: 0;
    font-size: 13px;
    line-height: 16px;
    color: var(--gray-strong);

    strong {
      font-weight: var(--font-bold);
    }
  }
}

.file-upload-error {
  display: flex;
  align-items: center;
  gap: var(--size-icon-xs);
  border-radius: var(--radius-base);

  .warning-icon {
    font-size: 20px;
    line-height: 1;
    color: var(--red-default);
    flex-shrink: 0;
  }

  span {
    font-size: var(--text-md);
    line-height: 20px;
    color: var(--red-default);
    font-weight: var(--font-medium);
  }
}

.ai-disclaimer-container {
  display: flex;
  justify-content: center;
  margin-top: var(--text-display-sm);
}

.btn-primary {
  display: flex;
  height: 48px;
  padding: var(--spacing-xl) var(--text-display-sm);
  align-items: center;
  gap: var(--spacing-xs);
  border-radius: var(--radius-base);
  font-size: var(--text-xl);
}

.form-actions {
  margin-top: 36px;
}

</style>
